<template>
  <div class="offline-boundary">
    <!-- Online, OR offline-but-cached → render the region normally. When
         offline the data is read-only because the write controls inside are
         gated by v-requires-connection / offline-disabled. -->
    <slot v-if="showContent" />

    <!-- Offline with no cached data → a graceful placeholder instead of a dead
         spinner or a raw error. Override with the `offline` slot. -->
    <slot v-else name="offline">
      <div class="offline-boundary__empty" :class="{ 'offline-boundary__empty--inline': inline }">
        <div class="offline-boundary__icon"><i :class="icon"></i></div>
        <div class="offline-boundary__title">{{ title }}</div>
        <div class="offline-boundary__msg">{{ message }}</div>
        <button type="button" class="offline-boundary__retry" @click="retry">
          <i class="fal fa-redo"></i>
          Try again
        </button>
      </div>
    </slot>
  </div>
</template>

<script>
import { useOfflineDetection } from '../../composables/useOfflineDetection';

/**
 * OfflineBoundary (TRIP-2666) — the "region" layer of graceful degradation.
 *
 * Wrap any data-dependent region (a modal body, ag-grid, sidebar, tab pane).
 * The consuming screen tells it whether the data that region needs is actually
 * available — typically because useQueryWithCache returned a cached payload:
 *
 *   <OfflineBoundary :ready="!!cachedTrip">
 *     <TripView :trip="cachedTrip" />
 *     <template #offline>…custom empty state…</template>
 *   </OfflineBoundary>
 *
 * Online → always renders the content. Offline → renders content only when
 * `ready` (cached), otherwise the fallback. `retry` emits a `retry` event if
 * the parent listens (re-run your query), else reloads the page.
 */
export default {
  name: 'OfflineBoundary',
  props: {
    /** Is the data this region needs available (e.g. present in the cache)? */
    ready: {
      type: Boolean,
      default: true,
    },
    /** Compact spacing for tight regions (sidebars, small panes). */
    inline: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: 'fal fa-wifi-slash',
    },
    title: {
      type: String,
      default: 'Not available offline',
    },
    message: {
      type: String,
      default: "You're offline, so we can't load this right now. It'll be back automatically when you reconnect.",
    },
  },
  setup() {
    const { isAppOffline } = useOfflineDetection();
    return { isAppOffline };
  },
  computed: {
    showContent() {
      return !this.isAppOffline || this.ready;
    },
  },
  methods: {
    retry() {
      if (this.$listeners && this.$listeners.retry) {
        this.$emit('retry');
      } else {
        window.location.reload();
      }
    },
  },
};
</script>

<style scoped>
.offline-boundary__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
  color: #899298;
}
.offline-boundary__empty--inline {
  padding: 24px 16px;
}
.offline-boundary__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(4, 123, 248, 0.09);
  color: #047bf8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-bottom: 16px;
}
.offline-boundary__empty--inline .offline-boundary__icon {
  width: 44px;
  height: 44px;
  font-size: 1.1rem;
  margin-bottom: 12px;
}
.offline-boundary__title {
  font-size: 1rem;
  font-weight: 600;
  color: #334152;
  margin-bottom: 6px;
}
.offline-boundary__msg {
  font-size: 0.85rem;
  line-height: 1.5;
  max-width: 320px;
  margin-bottom: 18px;
}
.offline-boundary__retry {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #047bf8;
  background: #047bf8;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
}
.offline-boundary__retry:hover {
  background: #0368d2;
}
</style>
